import { type Prisma } from '@prisma/client';
import { useLocation } from '@remix-run/react';
import { useLocale } from 'remix-i18next/react';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useTranslatedPath } from '#common/utils/i18n.tsx';
import { useVoicePath } from '#speaker-shop/hooks/useVoicePath';
import { StarRating } from './StarRating';

interface Opts {
    data: Prisma.TestimonialGetPayload<{ select: { company: true; name: true; id: true; text: true; rating: true } }>;
    maxTextLength?: number;
}

export function Testimonial({ data, maxTextLength }: Opts) {
    const locale = useLocale();
    const translatePath = useTranslatedPath(locale);
    const voicePath = useVoicePath();
    const url = translatePath(voicePath(`/testimonials#${data.id}`));
    const { t } = useEditableTranslation('hero');
    const location = useLocation();

    const isLongText = maxTextLength ? data.text.length > maxTextLength : false;
    const displayText = isLongText ? data.text.slice(0, maxTextLength).trimEnd() + '…' : data.text;

    return (
        <figure className='relative mx-auto text-center lg:mx-0 lg:text-left'>
            <div className='flex justify-center text-crispybluedark-700 lg:justify-start'>
                <a href={url} target={location.pathname === `/testimonials` ? '_self' : '_blank'}>
                    <StarRating rating={data.rating} />
                </a>
            </div>
            <blockquote className='mt-2'>
                <p className='font-display text-xl font-medium text-slate-900'>"{displayText}"</p>
                {isLongText && (
                    <a
                        target='_blank'
                        href={url}
                        className='mt-1 text-sm text-crispybluedark-700 hover:text-crispybluedark-900 underline cursor-pointer'
                    >
                        {t('read-full-testimonial')}
                    </a>
                )}
            </blockquote>
            <figcaption className='mt-2 text-sm text-slate-500'>
                <strong className="font-semibold text-crispybluedark-700 before:content-['—_']">{data.name}</strong>
                , {data.company}
            </figcaption>
        </figure>
    );
}
